import { useCallback, useEffect, useMemo, useState } from 'react';
import GridTable from '../../table/GridTable';
import { ColDef } from 'ag-grid-community';
import {
  IFortuneLabTrackingStats,
  useFortuneLabStatisticsStore,
} from '../../../store/useFortuneLabStatistics';
import {
  IFortuneLabCode,
  useFortuneLabCodeStore,
} from '../../../store/useFortuneLabCode';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import Select from '../../form/Select';

const StatisticsPage = () => {
  const { trackingStats, trackingStatsPage, fetchTrackingStats } =
    useFortuneLabStatisticsStore();
  const { codeList, fetchCodes } = useFortuneLabCodeStore();

  const [selectedTrkId, setSelectedTrkId] = useState<string>('');
  const [pageSize, setPageSize] = useState<string>('20');

  useEffect(() => {
    fetchCodes(0, 100);
  }, [fetchCodes]);

  const handleSearch = useCallback(() => {
    fetchTrackingStats(selectedTrkId || undefined, 0, Number(pageSize));
  }, [fetchTrackingStats, selectedTrkId, pageSize]);

  useEffect(() => {
    handleSearch();
  }, [handleSearch]);

  const handlePageChange = useCallback(
    (page: number) => {
      if (!trackingStatsPage) return;
      fetchTrackingStats(
        selectedTrkId || undefined,
        page,
        Number(pageSize)
      );
    },
    [fetchTrackingStats, selectedTrkId, pageSize, trackingStatsPage]
  );

  const handlePageSizeChange = useCallback((value: string) => {
    setPageSize(value);
  }, []);

  useEffect(() => {
    if (trackingStatsPage) {
      const { pageable, totalPages } = trackingStatsPage;
      if (pageable.pageNumber >= totalPages && totalPages > 0) {
        handlePageChange(Math.max(totalPages - 1, 0));
      }
    }
  }, [trackingStatsPage, handlePageChange]);

  const columns: ColDef<IFortuneLabTrackingStats>[] = useMemo(
    () => [
      {
        headerName: '트래킹 코드',
        field: 'trkId',
        flex: 1.5,
      },
      {
        headerName: '집계일',
        field: 'recordDate',
        flex: 1,
      },
      {
        headerName: '방문 수',
        field: 'landedUserCount',
        flex: 1,
        valueFormatter: params => params.value?.toLocaleString(),
      },
      {
        headerName: '결과 조회 수',
        field: 'resultUserCount',
        flex: 1,
        valueFormatter: params => params.value?.toLocaleString(),
      },
      {
        headerName: '생성 시각',
        field: 'createdAt',
        flex: 1.2,
        valueFormatter: params =>
          params.value ? new Date(params.value).toLocaleString() : '',
      },
      {
        headerName: '수정 시각',
        field: 'updatedAt',
        flex: 1.2,
        valueFormatter: params =>
          params.value ? new Date(params.value).toLocaleString() : '',
      },
    ],
    []
  );

  return (
    <div className="!w-full !h-full !flex !flex-col">
      <div className="!flex !items-center !justify-between !py-4">
        <div className="!flex !flex-wrap !items-center !gap-3">
          <Select
            style={{ width: 120 }}
            value={pageSize}
            onChange={event => handlePageSizeChange(event.target.value)}
            options={[
              { value: '20', option: '20개 보기' },
              { value: '50', option: '50개 보기' },
              { value: '100', option: '100개 보기' },
            ]}
          />
          <Select
            style={{ width: 240 }}
            value={selectedTrkId}
            onChange={event => setSelectedTrkId(event.target.value)}
            options={[
              { value: '', option: '전체 트래킹' },
              ...codeList.map(code => ({
                value: code.trkId,
                option: `${code.campaignName} (${code.trkId})`,
              })),
            ]}
          />
          <button
            className="!px-4 !py-2 !rounded !bg-blue-600 !text-white hover:!bg-blue-700"
            onClick={handleSearch}
          >
            조회
          </button>
        </div>
      </div>

      <div className="!flex-1 !min-h-0">
        <GridTable<IFortuneLabTrackingStats>
          height="100%"
          rowData={trackingStats}
          columnDefs={columns}
        />
      </div>

      {trackingStatsPage && trackingStatsPage.totalPages > 0 && (
        <div className="!w-full !mt-6">
          <div className="!flex !justify-center !items-center !space-x-2">
            <button
              className="!px-2 !py-1 !border !border-gray-300 !rounded hover:!bg-gray-50 disabled:!opacity-50"
              onClick={() => handlePageChange(0)}
              disabled={trackingStatsPage.pageable.pageNumber === 0}
            >
              <FontAwesomeIcon icon={faChevronLeft} />
              <FontAwesomeIcon icon={faChevronLeft} />
            </button>
            <button
              className="!px-2 !py-1 !border !border-gray-300 !rounded hover:!bg-gray-50 disabled:!opacity-50"
              onClick={() =>
                handlePageChange(
                  Math.max(0, trackingStatsPage.pageable.pageNumber - 1)
                )
              }
              disabled={trackingStatsPage.pageable.pageNumber === 0}
            >
              <FontAwesomeIcon icon={faChevronLeft} />
            </button>
            <span className="!text-sm !text-gray-600">
              {trackingStatsPage.pageable.pageNumber + 1} /{' '}
              {trackingStatsPage.totalPages || 1}
            </span>
            <button
              className="!px-2 !py-1 !border !border-gray-300 !rounded hover:!bg-gray-50 disabled:!opacity-50"
              onClick={() =>
                handlePageChange(
                  Math.min(
                    trackingStatsPage.totalPages - 1,
                    trackingStatsPage.pageable.pageNumber + 1
                  )
                )
              }
              disabled={
                trackingStatsPage.pageable.pageNumber >=
                trackingStatsPage.totalPages - 1
              }
            >
              <FontAwesomeIcon icon={faChevronRight} />
            </button>
            <button
              className="!px-2 !py-1 !border !border-gray-300 !rounded hover:!bg-gray-50 disabled:!opacity-50"
              onClick={() =>
                handlePageChange(
                  Math.max(trackingStatsPage.totalPages - 1, 0)
                )
              }
              disabled={
                trackingStatsPage.pageable.pageNumber >=
                trackingStatsPage.totalPages - 1
              }
            >
              <FontAwesomeIcon icon={faChevronRight} />
              <FontAwesomeIcon icon={faChevronRight} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default StatisticsPage;
